import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { Plus, Copy, ExternalLink, ArrowUpDown, Inbox } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import type { Deal, DealStatus, EngagementType, BillingRecord } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { getLastDeal } from '@/lib/lastDeal'
import { localeDefaultCurrency } from '@/lib/currency'
import { VERTICALS, OTHER_VERTICAL } from '@/lib/verticals'
import { PRICING_ENABLED } from '@/lib/pricing'
import { PendingRequestsPanel } from '@/components/PendingRequestsPanel'
import { DocumentList } from '@/components/DocumentList'
import { tileStyle } from '@/utils/palette'

// ── Constants ──────────────────────────────────────────────────────────────────

const INACTIVE: DealStatus[] = ['delivered', 'closed', 'withdrawn']

const STATUS_LABELS: Record<DealStatus, string> = {
  setup: 'Setup',
  seller_intake: 'Seller Intake',
  in_analysis: 'In Analysis',
  report_ready: 'Report Ready',
  delivered: 'Delivered',
  closed: 'Closed',
  withdrawn: 'Withdrawn',
}

const STATUS_COLORS: Record<string, string> = {
  setup: '#94a3b8',
  seller_intake: '#60a5fa',
  in_analysis: '#1e3a5f',
  report_ready: '#34d399',
  delivered: '#a3e635',
  closed: '#d1d5db',
  withdrawn: '#f87171',
}

const STATUS_LIST: DealStatus[] = [
  'setup', 'seller_intake', 'in_analysis', 'report_ready', 'delivered', 'closed', 'withdrawn',
]

type SortCol = 'business_name' | 'status' | 'loi_price' | 'created_at' | 'docs'
type SortDir = 'asc' | 'desc'

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n: number | null): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(n)
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function sortDeals(deals: Deal[], col: SortCol, dir: SortDir): Deal[] {
  return [...deals].sort((a, b) => {
    const va = col === 'business_name' ? a.business_name
      : col === 'status' ? a.status
      : col === 'loi_price' ? a.loi_price
      : col === 'created_at' ? a.created_at
      : a.docs_received
    const vb = col === 'business_name' ? b.business_name
      : col === 'status' ? b.status
      : col === 'loi_price' ? b.loi_price
      : col === 'created_at' ? b.created_at
      : b.docs_received
    if (va === null) return 1
    if (vb === null) return -1
    const cmp = va < vb ? -1 : va > vb ? 1 : 0
    return dir === 'asc' ? cmp : -cmp
  })
}

// ── Animation variants ─────────────────────────────────────────────────────────

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
}

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

// ── KpiCard ────────────────────────────────────────────────────────────────────

/** UI-PALETTE-1: `tone` picks the palette tile tone; title, value and subtitle take its text colour. */
export function KpiCard({ title, value, subtitle, unavailable, tone }: {
  title: string
  value: string | number
  subtitle?: string
  unavailable?: boolean
  tone: number
}) {
  const style = tileStyle(tone)
  return (
    <motion.div variants={cardVariants}>
      <Card style={style}>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-medium" style={{ color: style.color }}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {unavailable ? (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Not available
            </span>
          ) : (
            <p className="text-2xl font-bold tabular-nums" style={{ color: style.color }}>{value}</p>
          )}
          {subtitle && <p className="text-xs mt-1" style={{ color: style.color }}>{subtitle}</p>}
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ── CreateDealDialog ───────────────────────────────────────────────────────────

interface CreateDealForm {
  business_name: string
  engagement_type: EngagementType
  vertical: string
  fee: string
  pricing_model: string
  delivery_type: string
}

// ENGAGE-SIZING-1: the industry is captured here as well as on the full
// engagement form — a deal created from this quick dialog otherwise fell back to
// the general-SMB benchmark with nothing on screen to say so.
const EMPTY_DEAL_FORM: CreateDealForm = {
  business_name: '',
  engagement_type: 'sell_side',
  vertical: OTHER_VERTICAL,
  fee: '',
  pricing_model: 'model_c',
  delivery_type: 'yansar_delivers',
}

const NATIVE_SELECT = 'flex h-8 w-full rounded-lg border border-input bg-transparent px-2 text-sm outline-none focus:ring-1 focus:ring-ring'

function CreateDealDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onCreated: () => void
}) {
  const { user } = useAuth()
  const [form, setForm] = useState<CreateDealForm>(EMPTY_DEAL_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // analyst_name is required by the API — it is the conflict-of-interest key.
    const analystName = (user?.name || user?.email || '').trim()
    if (!analystName) {
      setError('We could not identify the analyst on your account. Please sign out and sign back in, then try again.')
      return
    }

    setSaving(true)
    try {
      await apiClient.post('/qoe/deals', {
        business_name: form.business_name,
        analyst_name: analystName,
        engagement_type: form.engagement_type,
        vertical: form.vertical,
        engagement_fee: form.fee ? parseFloat(form.fee) : null,
        pricing_model: form.pricing_model,
        delivery_type: form.delivery_type,
        // CURRENCY-1: seed the engagement's reporting currency from the CPA's
        // own browser locale. Editable afterwards on the workspace.
        currency: localeDefaultCurrency(),
      })
      toast.success(`Deal created: ${form.business_name}`)
      onOpenChange(false)
      onCreated()
      setForm(EMPTY_DEAL_FORM)
    } catch (err: unknown) {
      const status = (err as { response?: { status: number } })?.response?.status
      if (status === 409) setError(`A deal for "${form.business_name}" already exists.`)
      else setError(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Engagement</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1">
            <Label htmlFor="cd-biz">Business name</Label>
            <Input
              id="cd-biz"
              value={form.business_name}
              onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              placeholder="Acme Corp"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cd-eng">Engagement type</Label>
            <select
              id="cd-eng"
              value={form.engagement_type}
              onChange={e => setForm(f => ({ ...f, engagement_type: e.target.value as EngagementType }))}
              className={NATIVE_SELECT}
            >
              <option value="sell_side">Sell-side</option>
              <option value="buy_side">Buy-side</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cd-vertical">Industry</Label>
            <select
              id="cd-vertical"
              value={form.vertical}
              onChange={e => setForm(f => ({ ...f, vertical: e.target.value }))}
              className={NATIVE_SELECT}
            >
              {VERTICALS.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Sets the industry benchmark and tailors the seller's document request list.
            </p>
          </div>
          {PRICING_ENABLED && (
            <div className="space-y-1">
              <Label htmlFor="cd-fee">Fee (USD)</Label>
              <Input
                id="cd-fee"
                type="number"
                min="0"
                step="100"
                placeholder="5000"
                value={form.fee}
                onChange={e => setForm(f => ({ ...f, fee: e.target.value }))}
              />
            </div>
          )}
          {PRICING_ENABLED && (
            <div className="space-y-1">
              <Label htmlFor="cd-pricing">Pricing model</Label>
              <select
                id="cd-pricing"
                value={form.pricing_model}
                onChange={e => setForm(f => ({ ...f, pricing_model: e.target.value }))}
                className={NATIVE_SELECT}
              >
                <option value="model_c">Model C (Rev-share)</option>
                <option value="model_b">Model B (Fixed fee)</option>
              </select>
            </div>
          )}
          {PRICING_ENABLED && form.pricing_model === 'model_c' && (
            <div className="space-y-1">
              <Label htmlFor="cd-delivery">Delivery type</Label>
              <select
                id="cd-delivery"
                value={form.delivery_type}
                onChange={e => setForm(f => ({ ...f, delivery_type: e.target.value }))}
                className={NATIVE_SELECT}
              >
                <option value="yansar_delivers">Yansar-delivered</option>
                <option value="joint">Joint delivery</option>
              </select>
            </div>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {saving ? 'Creating…' : 'Create deal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── BillingPanel ───────────────────────────────────────────────────────────────

function BillingPanel({ records }: { records: BillingRecord[] }) {
  if (!Array.isArray(records) || records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">No billing records yet.</p>
    )
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Business</TableHead>
          <TableHead>Firm</TableHead>
          {PRICING_ENABLED && <TableHead className="text-right">Fee</TableHead>}
          {PRICING_ENABLED && <TableHead className="text-right">Yansar share</TableHead>}
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.slice(0, 10).map(r => (
          <TableRow key={r.billing_id}>
            <TableCell className="font-medium">{r.business_name}</TableCell>
            <TableCell className="text-muted-foreground">{r.firm}</TableCell>
            {PRICING_ENABLED && <TableCell className="text-right tabular-nums">{fmt(r.fee)}</TableCell>}
            {PRICING_ENABLED && <TableCell className="text-right tabular-nums">{fmt(r.yansar_share)}</TableCell>}
            <TableCell>
              <Badge variant={
                r.status === 'paid' ? 'default'
                  : r.status === 'invoiced' ? 'secondary'
                  : 'outline'
              }>
                {r.status}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// ── Dashboard (main) ───────────────────────────────────────────────────────────

export function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isSuperAdmin = user?.role === 'super_admin'
  const isAnalyst = user?.role === 'analyst'
  // DEFECT-B: mirrors the POST /qoe/deals guard and the /engagements/new route
  // gate. The old check only hid the button from analysts, so a `firm` user got
  // a New Deal dialog that the API now (correctly) rejects with a 403.
  const canCreateDeal = user?.role === 'cpa' || user?.role === 'super_admin'
  const lastDeal = useMemo(() => getLastDeal(), [])

  const { data: dealsResponse, loading, execute: fetchDeals } = useApi<{ deals: Deal[]; count: number }>()
  const { data: billing, execute: fetchBilling } = useApi<BillingRecord[]>()
  const { data: accessRequests, execute: fetchAccessRequests } = useApi<{ requests: unknown[]; count: number }>()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<DealStatus | ''>('')
  const [sortCol, setSortCol] = useState<SortCol>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [createOpen, setCreateOpen] = useState(false)
  const [recentDocs, setRecentDocs] = useState<any[]>([])

  const refetch = useCallback(() => {
    fetchDeals(() => apiClient.get<{ deals: Deal[]; count: number }>('/qoe/deals').then(r => r.data))
  }, [fetchDeals])

  useEffect(() => { refetch() }, [refetch])

  useEffect(() => {
    apiClient.get<any[]>('/v1/documents/recent?limit=5')
      .then(r => setRecentDocs(r.data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!isSuperAdmin || !PRICING_ENABLED) return
    // GET /qoe/billing returns {records, count}, not a bare array. Reading
    // r.data handed BillingPanel an object; its Array.isArray guard then showed
    // "No billing records yet." to a super_admin who did have records.
    fetchBilling(() =>
      apiClient.get<{ records: BillingRecord[]; count: number }>('/qoe/billing')
        .then(r => r.data.records ?? [])
        .catch(() => [] as BillingRecord[])
    )
  }, [isSuperAdmin, fetchBilling])

  useEffect(() => {
    if (!isSuperAdmin) return
    fetchAccessRequests(() =>
      apiClient.get<{ requests: unknown[]; count: number }>('/v1/admin/access-requests')
        .then(r => r.data)
        .catch(() => ({ requests: [], count: 0 }))
    )
  }, [isSuperAdmin, fetchAccessRequests])

  const allDeals = useMemo(() => dealsResponse?.deals ?? [], [dealsResponse])

  // ITEM 1: an analyst's dashboard is "My Work" — only the deals assigned to
  // them. Every KPI, the status donut and the pipeline table all read dealList,
  // so scoping here scopes the whole page.
  const dealList = useMemo(() => {
    if (!isAnalyst) return allDeals
    const me = [user?.name, user?.email]
      .filter((s): s is string => !!s)
      .map(s => s.toLowerCase())
    return allDeals.filter(d => d.analyst && me.includes(d.analyst.toLowerCase()))
  }, [allDeals, isAnalyst, user?.name, user?.email])

  // KPI computation
  const activeCount = dealList.filter(d => !INACTIVE.includes(d.status)).length
  const thisMonth = new Date().toISOString().slice(0, 7)
  const reportsThisMonth = dealList.filter(
    d => d.status === 'delivered' && d.created_at.startsWith(thisMonth)
  ).length
  const docsPendingReview = dealList.filter(d => d.status === 'seller_intake').length
  const pendingAccessRequestsCount = accessRequests?.count ?? 0

  // Donut chart data
  const chartData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const d of dealList) {
      counts.set(d.status, (counts.get(d.status) ?? 0) + 1)
    }
    return Array.from(counts.entries()).map(([name, value]) => ({ name, value }))
  }, [dealList])

  // Filtered + sorted rows
  const filteredDeals = useMemo(() => {
    let rows = dealList
    if (search.trim()) {
      const q = search.toLowerCase()
      rows = rows.filter(d =>
        d.business_name.toLowerCase().includes(q) ||
        (d.firm ?? '').toLowerCase().includes(q)
      )
    }
    if (statusFilter) rows = rows.filter(d => d.status === statusFilter)
    return sortDeals(rows, sortCol, sortDir)
  }, [dealList, search, statusFilter, sortCol, sortDir])

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const copySellerLink = (deal: Deal) => {
    if (!deal.seller_token) return
    const url = `${window.location.origin}/seller?token=${deal.seller_token}`
    navigator.clipboard.writeText(url).then(() => toast.success('Seller link copied'))
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
            {isAnalyst ? 'My Work' : 'CPA Dashboard'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.name ?? 'Analyst'} · {user?.firm ?? ''}
            {isAnalyst && ' · deals assigned to you'}
          </p>
        </div>
        {canCreateDeal && (
          <Button
            onClick={() => setCreateOpen(true)}
            style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Deal
          </Button>
        )}
      </div>

      {/* Resume last deal — analyst landing shortcut (ITEM 1) */}
      {isAnalyst && lastDeal && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Resume last deal
              </p>
              <p className="font-medium truncate">{lastDeal.business_name}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/deals/${lastDeal.deal_id}`)}
            >
              Open
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <KpiCard
          title="Active Deals"
          tone={0}
          value={loading ? '—' : activeCount}
          subtitle="excludes delivered / closed"
        />
        <KpiCard
          title="Docs Pending Review"
          tone={1}
          value={loading ? '—' : docsPendingReview}
          subtitle="deals in seller intake"
        />
        <KpiCard
          title="Reports Delivered MTD"
          tone={2}
          value={loading ? '—' : reportsThisMonth}
          subtitle={new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
        />
        <KpiCard
          title="Pending Access Requests"
          tone={3}
          value={pendingAccessRequestsCount}
          subtitle={isSuperAdmin ? 'awaiting approval' : 'super admin only'}
          unavailable={!isSuperAdmin}
        />
      </motion.div>

      {/* Table + donut chart */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Deal pipeline table */}
        <Card className="flex-1 min-w-0">
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <CardTitle>Deal Pipeline</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Input
                  placeholder="Search deals…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="h-8 w-48 text-sm"
                />
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as DealStatus | '')}
                  className="h-8 rounded-lg border border-input bg-transparent px-2 text-sm outline-none"
                >
                  <option value="">All statuses</option>
                  {STATUS_LIST.map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button
                      onClick={() => handleSort('business_name')}
                      className="flex items-center gap-1 text-left text-foreground"
                    >
                      Business <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-1 text-foreground"
                    >
                      Status <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </TableHead>
                  <TableHead>Analyst</TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('loi_price')}
                      className="flex items-center gap-1 text-foreground"
                    >
                      LOI <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('docs')}
                      className="flex items-center gap-1 text-foreground"
                    >
                      Docs <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('created_at')}
                      className="flex items-center gap-1 text-foreground"
                    >
                      Created <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredDeals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12">
                      {dealList.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 text-center">
                          <Inbox className="h-9 w-9 text-muted-foreground/40" />
                          <div>
                            <p className="text-sm font-medium text-foreground">No deals yet</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {canCreateDeal
                                ? 'Create your first engagement to start tracking the pipeline.'
                                : 'Engagements appear here once a CPA on your firm creates one.'}
                            </p>
                          </div>
                          {canCreateDeal && (
                            <Button
                              size="sm"
                              className="btn-gold mt-1"
                              onClick={() => setCreateOpen(true)}
                            >
                              <Plus className="mr-1.5 h-3.5 w-3.5" />
                              New Deal
                            </Button>
                          )}
                        </div>
                      ) : (
                        <p className="text-center text-sm text-muted-foreground">
                          No deals match the current filters.
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeals.map(deal => (
                    <TableRow
                      key={deal.deal_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/deals/${deal.deal_id}`)}
                    >
                      <TableCell className="font-medium max-w-[160px] truncate">
                        <span className="font-medium text-blue-600">
                          {deal.business_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`badge-deal-${deal.status}`}>
                          {STATUS_LABELS[deal.status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {deal.analyst ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmt(deal.loi_price)}
                      </TableCell>
                      <TableCell className="min-w-[96px]">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={deal.docs_total > 0
                              ? Math.round((deal.docs_received / deal.docs_total) * 100)
                              : 0}
                            className="w-12"
                          />
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {deal.docs_received}/{deal.docs_total}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(deal.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {deal.seller_token && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Copy seller link"
                              onClick={() => copySellerLink(deal)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title="Open in QoE Workspace"
                            onClick={() => navigate(`/normalization?deal_id=${deal.deal_id}`)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Donut chart */}
        <Card className="w-full lg:w-72 shrink-0">
          <CardHeader>
            <CardTitle className="text-base">Pipeline by Status</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-48 w-full rounded-full" />
            ) : dealList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                No deals to chart.
              </p>
            ) : (
              <div className="relative">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {chartData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={STATUS_COLORS[entry.name] ?? '#94a3b8'}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => {
                        const label = STATUS_LABELS[String(name) as DealStatus] ?? String(name)
                        return [`${value}`, label]
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div
                  style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <span style={{ fontSize: '22px', fontWeight: 700, color: '#0d1f3c', lineHeight: 1 }}>
                    {dealList.length}
                  </span>
                  <span style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                    {dealList.length === 1 ? 'deal' : 'deals'}
                  </span>
                </div>
              </div>
            )}
            <div className="mt-3 space-y-1.5">
              {chartData.map(entry => (
                <div key={entry.name} className="flex items-center gap-2 text-xs">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: STATUS_COLORS[entry.name] ?? '#94a3b8' }}
                  />
                  <span className="flex-1 text-muted-foreground">
                    {STATUS_LABELS[entry.name as DealStatus] ?? entry.name}
                  </span>
                  <span className="font-medium tabular-nums">{entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Super admin: billing summary — fee-only surface, hidden with QOE_PRICING_ENABLED */}
      {isSuperAdmin && PRICING_ENABLED && (
        <Card>
          <CardHeader>
            <CardTitle>Billing Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <BillingPanel records={billing ?? []} />
          </CardContent>
        </Card>
      )}

      {/* Super admin: pending access requests */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Access Requests</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <PendingRequestsPanel />
          </CardContent>
        </Card>
      )}

      {/* Recent documents */}
      <Card>
        <CardHeader style={{ paddingBottom: '8px' }}>
          <CardTitle style={{ fontSize: '13px', fontWeight: 500, color: '#0d1f3c' }}>
            Recent Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentList
            docs={recentDocs}
            onEmpty={
              <div style={{ textAlign: 'center', padding: '16px', fontSize: '13px', color: '#888' }}>
                No documents yet. Send the seller their portal link, or attach
                files yourself under Source Documents on the deal.
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* Create deal dialog */}
      <CreateDealDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refetch}
      />
    </div>
  )
}
